// src/components/pdf-editor/Toolbar.js
/**
 * PDF Editor Toolbar v2 – Clean action bar
 */
import React from 'react';

const BTN = {
  base: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(148,163,184,0.35)',
    background: 'rgba(15,23,42,0.55)', color: '#e2e8f0', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
  },
  active: {
    background: 'rgba(14,165,233,0.22)', color: '#67e8f9', borderColor: 'rgba(34,211,238,0.65)',
  },
  disabled: {
    opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none',
  },
  primary: {
    background: 'linear-gradient(90deg, rgba(14,165,233,0.35), rgba(59,130,246,0.3))', color: '#e0f2fe', borderColor: 'rgba(34,211,238,0.65)',
  },
};

const ToolBtn = ({ icon, label, onClick, active, disabled, primary, title }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title || label}
    style={{
      ...BTN.base,
      ...(active ? BTN.active : {}),
      ...(disabled ? BTN.disabled : {}),
      ...(primary && !active ? BTN.primary : {}),
    }}
  >
    {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
    {label && <span>{label}</span>}
  </button>
);

const Divider = () => (
  <div style={{ width: 1, height: 28, background: 'rgba(148,163,184,0.28)', margin: '0 4px' }} />
);

const Toolbar = ({
  zoom, onZoomIn, onZoomOut, onZoomReset,
  canUndo, canRedo, onUndo, onRedo,
  onSave, onDownload,
  onAddText, onAddImage, onOpenSignature,
  mode, setMode, hasPdf,
}) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '8px 16px', background: 'rgba(2,6,23,0.76)',
    borderBottom: '1px solid rgba(34,211,238,0.2)', flexWrap: 'wrap',
    backdropFilter: 'blur(10px)',
  }}>
    {/* Save / Download */}
    <ToolBtn icon="💾" label="Save" onClick={onSave} disabled={!hasPdf} primary title="Save changes (Ctrl+S)" />
    <ToolBtn icon="📥" label="Download" onClick={onDownload} disabled={!hasPdf} title="Download edited PDF" />

    <Divider />

    {/* Undo / Redo */}
    <ToolBtn icon="↩" onClick={onUndo} disabled={!canUndo} title="Undo (Ctrl+Z)" />
    <ToolBtn icon="↪" onClick={onRedo} disabled={!canRedo} title="Redo (Ctrl+Y)" />

    <Divider />

    {/* Mode buttons */}
    <ToolBtn icon="🖱" label="Select" onClick={() => setMode('select')}
      active={mode === 'select'} disabled={!hasPdf} />
    <ToolBtn icon="T" label="Add Text" onClick={onAddText}
      active={mode === 'add-text'} disabled={!hasPdf} />
    <ToolBtn icon="🖼" label="Add Image" onClick={onAddImage}
      active={mode === 'add-image'} disabled={!hasPdf} />
    <ToolBtn icon="✍" label="Sign" onClick={onOpenSignature}
      active={mode === 'add-signature'} disabled={!hasPdf} />

    <Divider />

    {/* Zoom */}
    <ToolBtn icon="−" onClick={onZoomOut} disabled={zoom <= 0.3} title="Zoom out" />
    <button
      onClick={onZoomReset}
      title="Reset zoom"
      style={{
        ...BTN.base, minWidth: 52, justifyContent: 'center',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {Math.round(zoom * 100)}%
    </button>
    <ToolBtn icon="+" onClick={onZoomIn} disabled={zoom >= 4} title="Zoom in" />
  </div>
);

export default Toolbar;
